import React, { useEffect, useRef } from 'react';
import HomeHeader from '@/components/HomeHeader';
import Footer from '@/components/homepage/Footer';
import { usePageMeta } from '@/hooks/usePageMeta';
import TableOfContents from '@/components/resources/TableOfContents';
import EmailCapture from '@/components/resources/EmailCapture';

const TITLE = "How to Raise Missionary Support When You Don't Have a Big Network";
const DESCRIPTION =
    "A practical guide for missionaries who feel like they don't know enough people — how to grow a small starting circle into full support through introductions instead of a bigger address book.";

const JSON_LD = {
    '@context': 'https://schema.org',
    '@graph': [
        {
            '@type': 'Article',
            headline: TITLE,
            description: DESCRIPTION,
            image: 'https://devotionfund.com/resources/cover31.jpg',
            datePublished: '2026-07-28',
            dateModified: '2026-07-28',
            author: { '@type': 'Organization', name: 'Devotion Fund', url: 'https://devotionfund.com' },
            publisher: {
                '@type': 'Organization',
                name: 'Devotion Fund',
                logo: { '@type': 'ImageObject', url: 'https://devotionfund.com/logo.png' },
            },
            mainEntityOfPage: {
                '@id': 'https://devotionfund.com/resources/raising-support-with-no-network',
                '@type': 'WebPage',
            },
        },
        {
            '@type': 'FAQPage',
            mainEntity: [
                {
                    '@type': 'Question',
                    name: 'Can I raise full missionary support if I only know a few dozen people?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes — most missionaries who feel like they have a small network actually have enough contacts to start, because support raising grows through introductions, not just direct asks. A starting list of 30-40 names, each asked for both a gift and a referral, regularly grows into a full support base within a few months.",
                    },
                },
                {
                    '@type': 'Question',
                    name: "What if I'm new to my faith and don't have a long church history to draw on?",
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: 'Your current church, small group, and any Christian community you\'ve joined recently are a legitimate starting circle, even if you\'ve only been part of it for a short time. Pastors and small-group leaders are often willing to introduce a new missionary to people in the wider congregation who are looking for a cause to support.',
                    },
                },
                {
                    '@type': 'Question',
                    name: 'Is it okay to ask people I only know casually, like former coworkers?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Yes. Casual relationships — former coworkers, college acquaintances, neighbors from a previous move — are exactly the layer that expands a small network, and many people are glad to be asked even from a lighter connection. A short, honest note explaining who you are and what you're doing works well here.",
                    },
                },
                {
                    '@type': 'Question',
                    name: 'How many people should I ask for an introduction, not just a gift?',
                    acceptedAnswer: {
                        '@type': 'Answer',
                        text: "Ask everyone. Whether someone gives or not, ending the conversation with 'do you know one or two other people who might want to hear about this?' is what turns a small starting list into a full donor base — each new supporter typically introduces at least one or two more.",
                    },
                },
            ],
        },
    ],
};

export default function RaisingSupportWithNoNetwork() {
    usePageMeta(TITLE, DESCRIPTION, { path: '/resources/raising-support-with-no-network' });

    const articleRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const el = document.createElement('script');
        el.type = 'application/ld+json';
        el.setAttribute('data-no-network-jsonld', 'true');
        el.textContent = JSON.stringify(JSON_LD);
        document.head.appendChild(el);
        return () => {
            el.remove();
        };
    }, []);

    return (
        <div className="min-h-screen bg-white flex flex-col">
            <HomeHeader />
            <div className="w-full h-64 md:h-80 bg-gray-100 overflow-hidden">
                <img src="/resources/cover31.jpg" alt="A small group of friends sitting together, representing a missionary's starting circle of support" className="w-full h-full object-cover" />
            </div>
            <div className="max-w-3xl mx-auto px-4 py-12 w-full">
                <h1 className="text-3xl font-bold mb-6">{TITLE}</h1>
                <TableOfContents targetRef={articleRef} />
                <div className="prose max-w-none" ref={articleRef}>
                    <p>"I don't know enough people" is one of the most common reasons missionaries hesitate to start raising support, and it's almost never actually true. Full support rarely comes from a huge existing address book — it comes from a smaller starting circle that grows through introductions along the way. If your network feels small, the goal isn't to manufacture more contacts overnight; it's to treat every conversation as two opportunities, a gift and a referral, instead of one.</p>

                    <h2>Start with concentric circles, not a headcount goal</h2>

                    <p>Instead of asking "do I know enough people," map your actual circles: immediate family, close friends, your church, past coworkers, neighbors, and anyone you've served alongside. Most people who feel like they have "no network" have thirty to fifty names once they map it out this way — enough to start, even if it isn't enough to finish on its own.</p>

                    <h2>Your church family counts more than you think</h2>

                    <p>Even a church you've only attended for a year or two is a legitimate starting circle. Ask your pastor or small-group leader directly whether they'd be willing to introduce you to a few people in the congregation who care about missions — church leaders often know exactly who in the pews is looking for a cause to support, and a warm introduction from them carries real weight. See <a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">how to ask your church to support you</a> for the full conversation to have.</p>

                    <h2>Ask for an introduction every single time</h2>

                    <p>The single highest-leverage habit in support raising with a small network is ending every conversation — whether it results in a gift or not — with "do you know one or two people who might want to hear about this?" A network of forty people who each introduce you to one or two more effectively doubles or triples your reach without you having to find those names yourself.</p>

                    <h2>Reconnect with dormant relationships</h2>

                    <p>Former coworkers, college classmates you haven't spoken to in years, and people from a city you used to live in are all fair game, even if the relationship feels distant. A short, honest reintroduction — who you are now, what you're doing, and a specific ask — works better than most people expect, because many people are glad to be remembered and asked rather than assumed unavailable.</p>

                    <h2>Let one small group become ten conversations</h2>

                    <p>Rather than treating your network as a flat list of individuals, look for existing groups — a Bible study, a former sports team, a professional association, a neighborhood group — where one conversation with a leader can open the door to several individual asks at once. A single well-placed introduction to a group often outperforms weeks of asking individuals one by one.</p>

                    <h2>When a smaller network is genuinely fine</h2>

                    <p>Not every missionary needs a sprawling donor base. If your support goal is modest — because you're bi-vocational, joining a spouse's existing supporters, or serving part-time — a smaller, more concentrated network of committed monthly partners can fully cover it. See <a href="/resources/bi-vocational-missionary-support-raising">bi-vocational missionary support raising</a> for how a smaller number works when there's other income involved.</p>

                    <h2>Frequently asked questions</h2>

                    <p><strong>Can I raise full missionary support if I only know a few dozen people?</strong><br />
                    Yes — a starting list of 30-40 names, each asked for a gift and a referral, regularly grows into full support within a few months.</p>

                    <p><strong>What if I'm new to my faith and don't have a long church history to draw on?</strong><br />
                    Your current church and small group are a legitimate starting circle; ask leaders for introductions to others in the congregation.</p>

                    <p><strong>Is it okay to ask people I only know casually, like former coworkers?</strong><br />
                    Yes — casual relationships are exactly the layer that expands a small network, and a short, honest note works well.</p>

                    <p><strong>How many people should I ask for an introduction, not just a gift?</strong><br />
                    Everyone — ending each conversation with a request for one or two introductions is what turns a small list into full support.</p>

                    <h2>Next reads on Devotion Fund</h2>
                    <ul>
                        <li><a href="/resources/how-to-ask-your-church-to-support-you-as-a-missionary">How to Ask Your Church to Support You as a Missionary (Step-by-Step)</a></li>
                        <li><a href="/resources/single-missionary-support-raising">Single Missionary Support Raising: 9 Things That Are Different</a></li>
                        <li><a href="/resources/introverts-guide-to-support-raising">The Introvert's Guide to Support Raising</a></li>
                    </ul>

                    <h2>About the author</h2>
                    <p>We're the team behind <a href="/">Devotion Fund</a>. We wrote this after hearing "I just don't know enough people" from missionaries who, once they mapped their real circles, had more of a starting point than they realized.</p>
                    <p><small><em>Last updated: 2026-07-28.</em></small></p>
                </div>
                <div className="mt-8">
                    <EmailCapture page="/resources/raising-support-with-no-network" />
                </div>
            </div>
            <Footer />
        </div>
    );
}
